import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
    Autocomplete,
    Backdrop,
    Box,
    Button,
    CircularProgress,
    Grid,
    IconButton,
    Stack,
    TextField,
    Typography,
    Chip,
    Card,
    CardContent,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    InputAdornment
} from '@mui/material';
import {
    Send as SendIcon,
    Draw as DrawIcon,
    Clear as ClearIcon,
    ArrowBack as ArrowBackIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    ExpandMore as ExpandMoreIcon,
    ContentCopy as ContentCopyIcon,
    PersonSearch as PersonSearchIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { initializeDB } from '../../../config/db';
import { JOB_STORE, MEGGER_TEST_STORE, EMPLOYEE_STORE } from '../../../config/constants';
import SignatureModalWithAuto from '../../common/SignatureModalWithAuto';
import { generateUUID } from '../../../utils/uuid';
import Swal from 'sweetalert2';
import meggerTestService from '../../../services/meggerTestService';
import SelectSingleEmployeeModal from '../../shared/SelectSingleEmployeeModal';

const STATUS_DRAFT = 'draft';
const STATUS_SUBMITTED = 'submitted';

const MeggerTestForm = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const uuid = searchParams.get('uuid');

    const userFullName = localStorage.getItem('employeeFullName') || '';
    const userId = localStorage.getItem('employeeId') || '';

    const [jobs, setJobs] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [form, setForm] = useState({
        uuid: uuid || generateUUID(),
        jobCode: '',
        jobName: '',
        customer: '',
        location: '',
        testDate: dayjs().format('YYYY-MM-DD'),
        testReadings: [{
            id: generateUUID(),
            feederPanelId: '',
            breakerCircuitNumber: '',
            feederVoltage: '',
            phaseAToPhaseB: '',
            phaseAToPhaseC: '',
            phaseBToPhaseC: '',
            phaseAToNeutral: '',
            phaseBToNeutral: '',
            phaseCToNeutral: '',
            phaseAToGround: '',
            phaseBToGround: '',
            phaseCToGround: ''
        }],
        // Three signatures
        installerId: '',
        installerName: '',
        installerSignature: '',
        installerSignatureDate: dayjs().format('YYYY-MM-DD'),
        
        supervisorId: userId,
        supervisorName: userFullName,
        supervisorSignature: '',
        supervisorSignatureDate: dayjs().format('YYYY-MM-DD'),
        
        engineerDesigneeType: 'project', // 'project', 'field', 'designer'
        engineerDesigneeId: '',
        engineerDesigneeName: '',
        engineerDesigneeSignature: '',
        engineerDesigneeSignatureDate: dayjs().format('YYYY-MM-DD'),
        
        status: STATUS_DRAFT,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
    });
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [signatureModalOpen, setSignatureModalOpen] = useState(false);
    const [signatureFor, setSignatureFor] = useState(''); // 'installer', 'supervisor', 'engineer'
    const [employeeModalOpen, setEmployeeModalOpen] = useState(false);
    const [employeeModalFor, setEmployeeModalFor] = useState(''); // 'installer', 'supervisor', 'engineer'

    const saveTimer = useRef(null);
    const sigPadRef = useRef(null);

    // Sort jobs by Job number in descending order
    const sortedJobs = useMemo(() => {
        const jobsWithLabels = jobs.map(job => ({
            ...job,
            label: `${job.Job} - ${job.JobName}`
        }));
        return jobsWithLabels.sort((a, b) => {
            const jobA = parseInt(a.Job) || 0;
            const jobB = parseInt(b.Job) || 0;
            return jobB - jobA;
        });
    }, [jobs]);

    // Load jobs, employees and form data on mount
    useEffect(() => {
        const loadData = async () => {
            const db = await initializeDB();

            // Load jobs
            if (db.objectStoreNames.contains(JOB_STORE)) {
                const tx = db.transaction(JOB_STORE, 'readonly');
                const store = tx.objectStore(JOB_STORE);
                const jobsData = await store.getAll();
                setJobs(jobsData || []);
            }

            // Load employees
            if (db.objectStoreNames.contains(EMPLOYEE_STORE)) {
                const tx = db.transaction(EMPLOYEE_STORE, 'readonly');
                const store = tx.objectStore(EMPLOYEE_STORE);
                const allEmployees = await store.getAll();
                setEmployees(allEmployees || []);
            }

            if (uuid) {
                const existingForm = await meggerTestService.loadByUuid(uuid);
                if (existingForm) {
                    setForm(existingForm);
                    setSubmitted(existingForm.status === STATUS_SUBMITTED);
                }
            }
        };
        loadData();
    }, [uuid]);

    // Queue save with debounce
    const queueSave = (formData) => {
        if (saveTimer.current) {
            clearTimeout(saveTimer.current);
        }
        saveTimer.current = setTimeout(() => {
            saveDraft(formData);
        }, 1000);
    };

    // Save draft to IndexedDB
    const saveDraft = async (formData) => {
        try {
            await meggerTestService.saveDraft(formData);
        } catch (error) {
            console.error('Error saving draft:', error);
        }
    };

    // Handle field change
    const handleFieldChange = (field, value) => {
        const updated = { ...form, [field]: value };
        setForm(updated);
        queueSave(updated);
    };

    // Handle job selection
    const handleJobChange = (event, newValue) => {
        if (!newValue) {
            const updated = {
                ...form,
                jobCode: '',
                jobName: '',
                customer: '',
                location: ''
            };
            setForm(updated);
            queueSave(updated);
            return;
        }

        const updated = {
            ...form,
            jobCode: newValue.Job || '',
            jobName: newValue.JobName || '',
            customer: newValue.Customer || '',
            location: newValue.Location || newValue.location || ''
        };
        setForm(updated);
        queueSave(updated);
    };

    // Handle engineer designee type change
    const handleEngineerDesigneeTypeChange = (newValue) => {
        const updated = {
            ...form,
            engineerDesigneeType: newValue
        };
        setForm(updated);
        queueSave(updated);
    };

    // Handle signature accept
    const handleSignatureAccept = () => {
        if (!sigPadRef.current) {
            console.error('Signature pad ref not found');
            return;
        }

        const dataUrl = sigPadRef.current.toDataURL();
        const updated = {
            ...form,
            [signatureFor + 'Signature']: dataUrl,
            [signatureFor + 'SignatureDate']: dayjs().format('YYYY-MM-DD')
        };
        setForm(updated);
        queueSave(updated);
        setSignatureModalOpen(false);
    };

    // Clear signature
    const handleClearSignature = (signType) => {
        const updated = {
            ...form,
            [signType + 'Signature']: ''
        };
        setForm(updated);
        queueSave(updated);
    };

    // Handle select employee
    const handleSelectEmployee = (person) => {
        const updated = {
            ...form,
            [employeeModalFor + 'Id']: person.id || '',
            [employeeModalFor + 'Name']: person.FullName || person.name || ''
        };
        setForm(updated);
        queueSave(updated);
        setEmployeeModalOpen(false);
    };

    // Add test reading
    const handleAddReading = () => {
        const newReading = {
            id: generateUUID(),
            feederPanelId: '',
            breakerCircuitNumber: '',
            feederVoltage: '',
            phaseAToPhaseB: '',
            phaseAToPhaseC: '',
            phaseBToPhaseC: '',
            phaseAToNeutral: '',
            phaseBToNeutral: '',
            phaseCToNeutral: '',
            phaseAToGround: '',
            phaseBToGround: '',
            phaseCToGround: ''
        };
        const updated = {
            ...form,
            testReadings: [...form.testReadings, newReading]
        };
        setForm(updated);
        queueSave(updated);
    };

    // Update test reading
    const handleReadingChange = (index, field, value) => {
        const updated = { ...form };
        updated.testReadings[index][field] = value;
        setForm(updated);
        queueSave(updated);
    };

    // Delete test reading
    const handleDeleteReading = (index) => {
        const updated = { ...form };
        updated.testReadings.splice(index, 1);
        setForm(updated);
        queueSave(updated);
    };

    // Duplicate test reading
    const handleDuplicateReading = (index) => {
        const readingToDuplicate = form.testReadings[index];
        const newReading = {
            ...readingToDuplicate,
            id: generateUUID()
        };
        const updated = {
            ...form,
            testReadings: [...form.testReadings, newReading]
        };
        setForm(updated);
        queueSave(updated);
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};

        if (!form.jobCode) {
            newErrors.jobCode = 'Job is required';
        }

        // Validate signatures
        if (!form.installerSignature) newErrors.installerSignature = 'Installer signature is required';
        if (!form.installerName) newErrors.installerName = 'Installer name is required';
        
        if (!form.supervisorSignature) newErrors.supervisorSignature = 'Electrical Supervisor signature is required';
        if (!form.supervisorName) newErrors.supervisorName = 'Supervisor name is required';
        
        if (!form.engineerDesigneeSignature) newErrors.engineerDesigneeSignature = 'Project/Field Engineer/Designee signature is required';
        if (!form.engineerDesigneeName) newErrors.engineerDesigneeName = 'Engineer/Designee name is required';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle submit
    const handleSubmit = async () => {
        if (!validateForm()) {
            Swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: 'Please fill in all required fields'
            });
            return;
        }

        const result = await Swal.fire({
            title: 'Submit Form?',
            text: 'Once submitted, the form cannot be edited',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, submit it'
        });

        if (!result.isConfirmed) return;

        try {
            setSaving(true);
            const submitData = { ...form, status: STATUS_SUBMITTED };
            await meggerTestService.submit(submitData);

            // Delete from IndexedDB after successful submission
            await meggerTestService.delete(form.uuid);

            Swal.fire({
                icon: 'success',
                title: 'Success!',
                text: 'Megger Test form submitted successfully'
            });

            navigate('/electrical/forms/megger-test/list');
        } catch (error) {
            console.error('Error submitting form:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.response?.data?.message || 'Failed to submit form'
            });
        } finally {
            setSaving(false);
        }
    };

    const getSignatureName = (signType) => {
        return form[signType + 'Name'] || '';
    };

    return (
        <Box sx={{
            height: 'calc(100vh - 60px)',
            backgroundColor: '#f5f5f5',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
        }}>
            {/* Header */}
            <Box sx={{ backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', p: 1.5, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton onClick={() => navigate('/electrical/forms/megger-test/list')} edge="start">
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="h6" fontWeight={600}>MEGGER TEST REPORT</Typography>
                    </Box>
                    <Chip
                        label={submitted ? 'Submitted' : 'Draft'}
                        size="small"
                        sx={{
                            backgroundColor: submitted ? '#4caf50' : '#9e9e9e',
                            color: 'white'
                        }}
                    />
                </Box>
            </Box>

            {/* Form Content */}
            <Box sx={{ flex: 1, overflow: 'auto' }}>
                <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
                    {/* Job Information */}
                    <Accordion defaultExpanded sx={{
                        mb: 0.5,
                        '&.Mui-expanded': {
                            margin: '0 0 4px 0'
                        }
                    }}>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                                minHeight: '40px !important',
                                '& .MuiAccordionSummary-content': {
                                    margin: '6px 0 !important'
                                }
                            }}
                        >
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Job Information</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 1.5, pt: 1 }}>
                            <Grid container rowSpacing={1} columnSpacing={1}>
                                <Grid item xs={12} sm={4}>
                                    <Autocomplete
                                        options={sortedJobs}
                                        getOptionLabel={(option) => option.label || ''}
                                        value={sortedJobs.find(j => j.Job === form.jobCode) || null}
                                        onChange={handleJobChange}
                                        disabled={submitted}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                size="small"
                                                label="JOB NUMBER *"
                                                error={!!errors.jobCode}
                                                helperText={errors.jobCode}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="CUSTOMER"
                                        value={form.customer || ''}
                                        InputProps={{
                                            readOnly: true
                                        }}
                                        disabled={submitted}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="DATE"
                                        type="date"
                                        value={form.testDate}
                                        onClick={(e) => !submitted && e.target.showPicker && e.target.showPicker()}
                                        onChange={(e) => handleFieldChange('testDate', e.target.value)}
                                        disabled={submitted}
                                        InputLabelProps={{ shrink: true }}
                                        InputProps={{
                                            style: { cursor: submitted ? 'default' : 'pointer' }
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </AccordionDetails>
                    </Accordion>

                    {/* Test Readings */}
                    <Card elevation={1} sx={{ mb: 0.5 }}>
                        <CardContent sx={{ py: 1.75 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.25}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Test Readings</Typography>
                            </Box>
                            {form.testReadings.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                                    No test readings added yet. Click "Add Reading" to get started.
                                </Typography>
                            ) : (
                                <Stack spacing={1.25}>
                                    {form.testReadings.map((reading, index) => (
                                        <Card key={reading.id} variant="outlined" sx={{ bgcolor: '#fafafa' }}>
                                            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                                    <Typography variant="subtitle2" fontWeight={600}>
                                                        Reading #{index + 1}
                                                    </Typography>
                                                    {!submitted && (
                                                        <Box display="flex" gap={1.5}>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleDuplicateReading(index)}
                                                                color="primary"
                                                                title="Duplicate reading"
                                                            >
                                                                <ContentCopyIcon fontSize="small" />
                                                            </IconButton>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleDeleteReading(index)}
                                                                color="error"
                                                                title="Delete reading"
                                                                disabled={form.testReadings.length === 1}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Box>
                                                    )}
                                                </Box>
                                                <Grid container rowSpacing={1} columnSpacing={1}>
                                                    <Grid item xs={12} sm={4}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            label="Feeder Panel ID"
                                                            value={reading.feederPanelId}
                                                            onChange={(e) => handleReadingChange(index, 'feederPanelId', e.target.value)}
                                                            disabled={submitted}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={4}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            label="Breaker/Circuit Number"
                                                            value={reading.breakerCircuitNumber}
                                                            onChange={(e) => handleReadingChange(index, 'breakerCircuitNumber', e.target.value)}
                                                            disabled={submitted}
                                                            inputProps={{ inputMode: 'numeric' }}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={4}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            label="Feeder Voltage"
                                                            value={reading.feederVoltage}
                                                            onChange={(e) => handleReadingChange(index, 'feederVoltage', e.target.value)}
                                                            disabled={submitted}
                                                            inputProps={{ inputMode: 'numeric' }}
                                                        />
                                                    </Grid>
                                                    {/* Phase to Phase */}
                                                    <Grid item xs={12}><Typography variant="caption" color="text.secondary" fontWeight={600}>Phase to Phase</Typography></Grid>
                                                    <Grid item xs={12} sm={4}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            label="Phase A to Phase B"
                                                            value={reading.phaseAToPhaseB}
                                                            onChange={(e) => handleReadingChange(index, 'phaseAToPhaseB', e.target.value)}
                                                            disabled={submitted}
                                                            inputProps={{ inputMode: 'numeric' }}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={4}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            label="Phase A to Phase C"
                                                            value={reading.phaseAToPhaseC}
                                                            onChange={(e) => handleReadingChange(index, 'phaseAToPhaseC', e.target.value)}
                                                            disabled={submitted}
                                                            inputProps={{ inputMode: 'numeric' }}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={4}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            label="Phase B to Phase C"
                                                            value={reading.phaseBToPhaseC}
                                                            onChange={(e) => handleReadingChange(index, 'phaseBToPhaseC', e.target.value)}
                                                            disabled={submitted}
                                                            inputProps={{ inputMode: 'numeric' }}
                                                        />
                                                    </Grid>
                                                    {/* Phase to Neutral */}
                                                    <Grid item xs={12}><Typography variant="caption" color="text.secondary" fontWeight={600}>Phase to Neutral</Typography></Grid>
                                                    <Grid item xs={12} sm={4}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            label="Phase A to Neutral"
                                                            value={reading.phaseAToNeutral}
                                                            onChange={(e) => handleReadingChange(index, 'phaseAToNeutral', e.target.value)}
                                                            disabled={submitted}
                                                            inputProps={{ inputMode: 'numeric' }}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={4}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            label="Phase B to Neutral"
                                                            value={reading.phaseBToNeutral}
                                                            onChange={(e) => handleReadingChange(index, 'phaseBToNeutral', e.target.value)}
                                                            disabled={submitted}
                                                            inputProps={{ inputMode: 'numeric' }}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={4}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            label="Phase C to Neutral"
                                                            value={reading.phaseCToNeutral}
                                                            onChange={(e) => handleReadingChange(index, 'phaseCToNeutral', e.target.value)}
                                                            disabled={submitted}
                                                            inputProps={{ inputMode: 'numeric' }}
                                                        />
                                                    </Grid>
                                                    {/* Phase to Ground */}
                                                    <Grid item xs={12}><Typography variant="caption" color="text.secondary" fontWeight={600}>Phase to Ground</Typography></Grid>
                                                    <Grid item xs={12} sm={4}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            label="Phase A to Ground"
                                                            value={reading.phaseAToGround}
                                                            onChange={(e) => handleReadingChange(index, 'phaseAToGround', e.target.value)}
                                                            disabled={submitted}
                                                            inputProps={{ inputMode: 'numeric' }}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={4}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            label="Phase B to Ground"
                                                            value={reading.phaseBToGround}
                                                            onChange={(e) => handleReadingChange(index, 'phaseBToGround', e.target.value)}
                                                            disabled={submitted}
                                                            inputProps={{ inputMode: 'numeric' }}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={4}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            label="Phase C to Ground"
                                                            value={reading.phaseCToGround}
                                                            onChange={(e) => handleReadingChange(index, 'phaseCToGround', e.target.value)}
                                                            disabled={submitted}
                                                            inputProps={{ inputMode: 'numeric' }}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </CardContent>
                                        </Card>
                                    ))}
                                    {!submitted && form.testReadings.length > 0 && (
                                        <Box display="flex" justifyContent="flex-end" mt={1.5}>
                                            <Button
                                                variant="contained"
                                                startIcon={<AddIcon />}
                                                onClick={handleAddReading}
                                                size="small"
                                            >
                                                Add Reading
                                            </Button>
                                        </Box>
                                    )}
                                </Stack>
                            )}
                        </CardContent>
                    </Card>

                    {/* Signatures */}
                    <Card elevation={1} sx={{ mb: 0.5 }}>
                        <CardContent sx={{ py: 1.75 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>Signatures</Typography>
                            
                            {/* Installer Signature */}
                            <Box mb={2}>
                                <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>Installer</Typography>
                                <Grid container rowSpacing={1} columnSpacing={{ xs: 1, md: 1.5 }} alignItems="center">
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Installer *"
                                            value={form.installerName}
                                            onClick={() => {
                                                if (!submitted) {
                                                    setEmployeeModalFor('installer');
                                                    setEmployeeModalOpen(true);
                                                }
                                            }}
                                            InputProps={{
                                                readOnly: true,
                                                style: { cursor: submitted ? 'default' : 'pointer' },
                                                endAdornment: !submitted && (
                                                    <InputAdornment position="end">
                                                        <IconButton onClick={() => {
                                                            setEmployeeModalFor('installer');
                                                            setEmployeeModalOpen(true);
                                                        }} size="small">
                                                            <PersonSearchIcon fontSize="small" />
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            }}
                                            error={!!errors.installerName}
                                            helperText={errors.installerName}
                                            disabled={submitted}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Date"
                                            type="date"
                                            value={form.installerSignatureDate}
                                            onClick={(e) => !submitted && e.target.showPicker && e.target.showPicker()}
                                            onChange={(e) => handleFieldChange('installerSignatureDate', e.target.value)}
                                            disabled={submitted}
                                            InputLabelProps={{ shrink: true }}
                                            InputProps={{
                                                style: { cursor: submitted ? 'default' : 'pointer' }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Box display="flex" justifyContent="center" alignItems="center">
                                            {form.installerSignature ? (
                                                <Box position="relative" display="inline-block">
                                                    <img
                                                        src={form.installerSignature}
                                                        alt="Signature"
                                                        style={{
                                                            maxWidth: 200,
                                                            height: 60,
                                                            border: '1px solid #ddd',
                                                            borderRadius: 4,
                                                            cursor: submitted ? 'default' : 'pointer'
                                                        }}
                                                        onClick={() => {
                                                            if (!submitted) {
                                                                setSignatureFor('installer');
                                                                setSignatureModalOpen(true);
                                                            }
                                                        }}
                                                        title={submitted ? '' : 'Click to edit signature'}
                                                    />
                                                    {!submitted && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleClearSignature('installer')}
                                                            sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}
                                                        >
                                                            <ClearIcon sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    )}
                                                </Box>
                                            ) : (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={<DrawIcon />}
                                                    onClick={() => {
                                                        setSignatureFor('installer');
                                                        setSignatureModalOpen(true);
                                                    }}
                                                    disabled={submitted}
                                                >
                                                    Add Signature
                                                </Button>
                                            )}
                                        </Box>
                                        {errors.installerSignature && (
                                            <Typography color="error" variant="caption" display="block" mt={0.5} textAlign="center">
                                                {errors.installerSignature}
                                            </Typography>
                                        )}
                                    </Grid>
                                </Grid>
                            </Box>

                            {/* Electrical Supervisor Signature */}
                            <Box mb={2}>
                                <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>Electrical Supervisor</Typography>
                                <Grid container rowSpacing={1} columnSpacing={{ xs: 1, md: 1.5 }} alignItems="center">
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Supervisor *"
                                            value={form.supervisorName}
                                            onClick={() => {
                                                if (!submitted) {
                                                    setEmployeeModalFor('supervisor');
                                                    setEmployeeModalOpen(true);
                                                }
                                            }}
                                            InputProps={{
                                                readOnly: true,
                                                style: { cursor: submitted ? 'default' : 'pointer' },
                                                endAdornment: !submitted && (
                                                    <InputAdornment position="end">
                                                        <IconButton onClick={() => {
                                                            setEmployeeModalFor('supervisor');
                                                            setEmployeeModalOpen(true);
                                                        }} size="small">
                                                            <PersonSearchIcon fontSize="small" />
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            }}
                                            error={!!errors.supervisorName}
                                            helperText={errors.supervisorName}
                                            disabled={submitted}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Date"
                                            type="date"
                                            value={form.supervisorSignatureDate}
                                            onClick={(e) => !submitted && e.target.showPicker && e.target.showPicker()}
                                            onChange={(e) => handleFieldChange('supervisorSignatureDate', e.target.value)}
                                            disabled={submitted}
                                            InputLabelProps={{ shrink: true }}
                                            InputProps={{
                                                style: { cursor: submitted ? 'default' : 'pointer' }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Box display="flex" justifyContent="center" alignItems="center">
                                            {form.supervisorSignature ? (
                                                <Box position="relative" display="inline-block">
                                                    <img
                                                        src={form.supervisorSignature}
                                                        alt="Signature"
                                                        style={{
                                                            maxWidth: 200,
                                                            height: 60,
                                                            border: '1px solid #ddd',
                                                            borderRadius: 4,
                                                            cursor: submitted ? 'default' : 'pointer'
                                                        }}
                                                        onClick={() => {
                                                            if (!submitted) {
                                                                setSignatureFor('supervisor');
                                                                setSignatureModalOpen(true);
                                                            }
                                                        }}
                                                        title={submitted ? '' : 'Click to edit signature'}
                                                    />
                                                    {!submitted && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleClearSignature('supervisor')}
                                                            sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}
                                                        >
                                                            <ClearIcon sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    )}
                                                </Box>
                                            ) : (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={<DrawIcon />}
                                                    onClick={() => {
                                                        setSignatureFor('supervisor');
                                                        setSignatureModalOpen(true);
                                                    }}
                                                    disabled={submitted}
                                                >
                                                    Add Signature
                                                </Button>
                                            )}
                                        </Box>
                                        {errors.supervisorSignature && (
                                            <Typography color="error" variant="caption" display="block" mt={0.5} textAlign="center">
                                                {errors.supervisorSignature}
                                            </Typography>
                                        )}
                                    </Grid>
                                </Grid>
                            </Box>

                            {/* Project/Field Engineer/Designee Signature */}
                            <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>Project Engineer / Field Engineer / Designee</Typography>
                                <Grid container rowSpacing={1} columnSpacing={{ xs: 1, md: 1.5 }} alignItems="center">
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Engineer/Designee Name *"
                                            value={form.engineerDesigneeName}
                                            onChange={(e) => handleFieldChange('engineerDesigneeName', e.target.value)}
                                            error={!!errors.engineerDesigneeName}
                                            helperText={errors.engineerDesigneeName}
                                            disabled={submitted}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Date"
                                            type="date"
                                            value={form.engineerDesigneeSignatureDate}
                                            onClick={(e) => !submitted && e.target.showPicker && e.target.showPicker()}
                                            onChange={(e) => handleFieldChange('engineerDesigneeSignatureDate', e.target.value)}
                                            disabled={submitted}
                                            InputLabelProps={{ shrink: true }}
                                            InputProps={{
                                                style: { cursor: submitted ? 'default' : 'pointer' }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Box display="flex" justifyContent="center" alignItems="center">
                                            {form.engineerDesigneeSignature ? (
                                                <Box position="relative" display="inline-block">
                                                    <img
                                                        src={form.engineerDesigneeSignature}
                                                        alt="Signature"
                                                        style={{
                                                            maxWidth: 200,
                                                            height: 60,
                                                            border: '1px solid #ddd',
                                                            borderRadius: 4,
                                                            cursor: submitted ? 'default' : 'pointer'
                                                        }}
                                                        onClick={() => {
                                                            if (!submitted) {
                                                                setSignatureFor('engineerDesignee');
                                                                setSignatureModalOpen(true);
                                                            }
                                                        }}
                                                        title={submitted ? '' : 'Click to edit signature'}
                                                    />
                                                    {!submitted && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleClearSignature('engineerDesignee')}
                                                            sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}
                                                        >
                                                            <ClearIcon sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    )}
                                                </Box>
                                            ) : (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={<DrawIcon />}
                                                    onClick={() => {
                                                        setSignatureFor('engineerDesignee');
                                                        setSignatureModalOpen(true);
                                                    }}
                                                    disabled={submitted}
                                                >
                                                    Add Signature
                                                </Button>
                                            )}
                                        </Box>
                                        {errors.engineerDesigneeSignature && (
                                            <Typography color="error" variant="caption" display="block" mt={0.5} textAlign="center">
                                                {errors.engineerDesigneeSignature}
                                            </Typography>
                                        )}
                                    </Grid>
                                </Grid>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Submit Button */}
                    {!submitted && (
                        <Box display="flex" justifyContent="center" sx={{ mt: 2, mb: 2 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                size="large"
                                onClick={handleSubmit}
                                disabled={saving}
                                startIcon={saving ? <CircularProgress size={20} /> : <SendIcon />}
                                sx={{ minWidth: 200 }}
                            >
                                {saving ? 'Submitting...' : 'Submit'}
                            </Button>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Employee Selection Modal */}
            <SelectSingleEmployeeModal
                open={employeeModalOpen}
                onClose={() => {
                    setEmployeeModalOpen(false);
                    setEmployeeModalFor('');
                }}
                onSelect={handleSelectEmployee}
                employees={employees}
            />

            {/* Signature Modal */}
            <SignatureModalWithAuto
                open={signatureModalOpen}
                onClose={() => setSignatureModalOpen(false)}
                sigPadRef={sigPadRef}
                employeeName={getSignatureName(signatureFor)}
                onAccept={handleSignatureAccept}
                existingSignature={form[signatureFor + 'Signature']}
            />

            <Backdrop open={saving} sx={{ color: '#fff', zIndex: 9999 }}>
                <CircularProgress color="inherit" />
            </Backdrop>
        </Box>
    );
};

export default MeggerTestForm;

